import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import TextInput from '../../Input/TextInput';
import { AdapterProps, SearchAdapterProps } from '../types';
import InputLabel from '../../InputLabel/InputLabel';
import css from '../filters.module.scss';
import Button from '../../Button/Button';
import Icon from '../../Icon/Icon';
import clsx from 'clsx';

type Value = string;

const SearchAdapter = (
  props: AdapterProps<Value> & SearchAdapterProps<Value>,
) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { name, label, onChange, value, defaultValue = '' } = props;

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const debouncedOnChange = useMemo(
    () =>
      debounce((nextValue: string) => {
        onChangeRef.current?.(nextValue);
      }, 250),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedOnChange.cancel();
    };
  }, [debouncedOnChange]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      debouncedOnChange(event.target.value);
    },
    [debouncedOnChange],
  );

  const handleClear = useCallback(() => {
    debouncedOnChange.cancel();
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    onChangeRef.current?.('');
  }, [debouncedOnChange]);

  return (
    <div className={css.filter}>
      {label && <InputLabel className={css.label}>{label}</InputLabel>}
      <TextInput
        autoFocus={Boolean(value ?? defaultValue)}
        variant="filter"
        name={name}
        defaultValue={value ?? defaultValue}
        onChange={handleChange}
        ref={inputRef}
      />
      <Button
        tabIndex={-1}
        onClick={handleClear}
        variant="text"
        startIcon={<Icon name="fa fa-times" />}
        className={clsx(css['remove-button'], css['clear-button'])}
      />
    </div>
  );
};

export default SearchAdapter;
